<template>
  <div v-if="!isSessionChecked" class="h-100">
    <!-- Loading Indicator -->
    <app-loading />
  </div>
  <div v-else class="h-100">
    <component :is="layout" class="w-100 h-100">
      <router-view />
    </component>
    <validation-modal />
    <page-loading />
    <confirm-welcome-modal />
    <authenticator-modal />
    <transition :enter-active-class="'animate__animated animate__fadeIn'" :leave-active-class="'animate__animated animate__fadeOut'">
      <chat-hub-app
        v-if="isShowChatHub"
        :maximize-height="'calc(100% - 80px)'"
        :z-index="'1000'"
        :ai-name="'AI Assistant'"
        :username="currentUsername"
      />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/store/authStore';
import permissionService from '@/services/permission';
import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/router';
import dataSourceService from '@/services/dataSource';

// Import your stores and services
import { useBrandStore } from '@/store/brandStore';
import { useLoadingStore } from './store/loadingStore';
import { usePermissionStore } from './store/permissionStore';
import { useModuleStore } from './store/moduleStore';

import ValidationModal from './views/components/modal/ValidationModal.vue';
import AuthenticatorModal from '@/views/components/modal/MFAModal.vue';

import LayoutVertical from './views/layouts/vertical/LayoutVertical.vue';
import PageLoading from './views/components/loading/PageLoading.vue';
import AppLoading from './views/components/loading/AppLoading.vue';
import ConfirmWelcomeModal from '@/views/modules/permission/components/permission-modal/ConfirmWelcomeModal.vue';

import brandService from '@/services/brand';
import authService from '@/services/authentication';
import moduleService from '@/services/module';
import { signalRService } from '@/services/signalR';

import { useChat } from '@chococrm/chat-hub-app';

const brandStore = useBrandStore();
const loadingStore = useLoadingStore();
const permissionStore = usePermissionStore();
const moduleStore = useModuleStore();
const authStore = useAuthStore();

let intervalEvent: any = null;
const isShowChatHub = ref(false);
const currentToken = ref<string | null>(null);
const currentUsername = ref<string | null>(null);
const { setToken, setApplicationName, clearToken, clearApplicationName } = useChat();

const { appLoadingConfig } = storeToRefs(loadingStore);

const layout = computed(() => {
  if (router.currentRoute.value.meta.layout === 'full') return 'layout-full';
  return LayoutVertical;
});

// Reactive variable to control rendering
const isSessionChecked = ref(false);

const initializeStore = () => {
  // Load data from Local
  authStore.loadAuthInfoFromLocalStorage();
  brandStore.loadBrandInfoFromLocalStorage();
  moduleStore.loadModuleVersionsFromLocalStorage();
};

const initialAppState = async () => {
  initializeStore();

  try {
    // Fetch Module Version
    // Fetch and assign permissions
    await dataSourceService.fetchAndSetdataSources();
    await permissionService.fetchAndSetPermissions();
    await moduleService.fetchAndSetModuleVersions();
    // Fetch brand list
    const accessToken = authStore.access_token;
    if (!accessToken) throw new Error('Access token not set');
    const success = await brandService.fetchBrandListAndSet(accessToken);
    if (!success) throw new Error('Brand list not found');
    // Start SignalR service
    // await signalRService.start();
  } catch (error) {
    // If an error occurs, redirect to login
    console.error('Error during session check:', error);
    await authService.logout();
  } finally {
    // Session check complete
    isSessionChecked.value = true;
    appLoadingConfig.value.isOpen = false;
  }
};

onBeforeMount(async () => {
  const brandRefQuery = brandService.getBrandRefParams();
  if (authService.isUserLoggedIn()) {
    if (brandRefQuery && brandStore.getBrandRef !== brandRefQuery) {
      const success = await brandService.fetchSubmitBrandAndSet(brandRefQuery);
      // await new Promise((resolve) => setTimeout(resolve, 300));
      if (success) {
        await initialAppState();
      }
    } else {
      // initial normal flow
      await initialAppState();
    }
  } else {
    // User is not logged in, redirect to login
    appLoadingConfig.value.isOpen = false;

    await new Promise((resolve) => setTimeout(resolve, 500));
    isSessionChecked.value = true;
  }
});

onMounted(() => {
  if (intervalEvent == null) {
    intervalEvent = setInterval(() => {
      if (useAuthStore().$state.access_token != null && isShowChatHub.value == false && authStore.getFullName != null && authStore.getFullName != '') {
        setToken(useAuthStore().$state.access_token as string)
        setApplicationName('CDP')
        currentToken.value = useAuthStore().$state.access_token;
        currentUsername.value = authStore.getFullName;
        isShowChatHub.value = true;
      } else if (((useAuthStore().$state.access_token == null || useAuthStore().$state.access_token != currentToken.value) || (authStore.getFullName == null || authStore.getFullName != currentUsername.value)) && isShowChatHub.value == true) {
        clearToken();
        clearApplicationName();
        currentToken.value = null;
        currentUsername.value = null;
        isShowChatHub.value = false;
      }
    }, 500);
  }
});

onBeforeUnmount(() => {
  signalRService.removeVisibilityChangeListener();
});
onUnmounted(() => {
  signalRService.disconnect();
  permissionStore.$reset();
});
</script>
<style lang="scss">

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
  border: none;
}
::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 10px;
}

#app {
  font-family: 'Prompt', Avenir, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}

// Hide eye close and open input type password
input::-ms-reveal,
input::-ms-clear {
  display: none;
}
</style>
